/**
 * @file LeadsList.jsx
 * @author Rishikesh
 * @date 2024-09-24
 * @description Show the list of leads
 */
import React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Pagination,
} from "@mui/material";
import {
  ContentPasteSearchOutlined,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

export const LeadsList = ({
  isLoading,
  isTableLoading = false,
  leads,
  userList = [],
  page = 0,
  pageSize = 25,
  totalCount = 0,
  searchQuery = "",
  assigneeFilter = "",
  isdeletedFilter = "0",
  onSearchChange,
  onAssigneeFilterChange,
  onIsdeletedFilterChange,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const role = useSelector((state) => state.auth.user?.role);
  const isAdmin = role !== "employee" && role !== "agent";

  const getAssigneeName = (ownerid) => {
    if (!ownerid) return "—";
    return userList.find((user) => String(user.id) === String(ownerid))?.name ?? "—";
  };

  const columnCount = isAdmin ? 6 : role !== "agent" ? 5 : 4;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0;

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        rowGap={2}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          columnGap={2}
          rowGap={2}
          flexWrap="wrap"
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={490} height={24} />
          ) : (
            <Typography fontWeight={700} fontSize={24} color="#222222">
              Lead
            </Typography>
          )}
          {!isLoading && (
            <TextField
              id="outlined-start-adornment"
              placeholder="Search leads"
              className="inputRounded"
              color="black"
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentPasteSearchOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {!isLoading && isAdmin && (
            <TextField
              select
              label="Assign to"
              value={assigneeFilter}
              onChange={(e) => onAssigneeFilterChange?.(e.target.value)}
              size="small"
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            >
              <MenuItem value="">All assignees</MenuItem>
              {userList.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!isLoading && isAdmin && (
            <TextField
              select
              label="Deleted"
              value={isdeletedFilter}
              onChange={(e) => onIsdeletedFilterChange?.(e.target.value)}
              size="small"
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            >
              <MenuItem value="0">Not deleted</MenuItem>
              <MenuItem value="1">Deleted</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
          )}
        </Stack>
        {role !== "employee" &&
          (isLoading ? (
            <Skeleton variant="rounded" width={180} height={40} />
          ) : (
            <Button
              variant="contained"
              sx={{ borderRadius: "12px" }}
              onClick={() => navigate(`/leads/manage-lead/new`)}
            >
              Create Lead
            </Button>
          ))}
      </Stack>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 1,
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2 }}
            width="100%"
            height={400}
          />
        ) : (
          <>
            <TableContainer sx={{ mt: 2, opacity: isTableLoading ? 0.6 : 1 }}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: "#3A80F7",
                    }}
                  >
                    <TableCell sx={{ color: "white", borderTopLeftRadius: 8 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ color: "white" }}>Email</TableCell>
                    <TableCell sx={{ color: "white" }}>Enquiry Date</TableCell>
                    <TableCell align="center" sx={{ color: "white" }}>
                      Status
                    </TableCell>
                    {isAdmin ? (
                      <TableCell sx={{ color: "white" }}>Assignee</TableCell>
                    ) : null}
                    {role !== "agent" ? (
                      <TableCell
                        sx={{
                          color: "white",
                          borderTopRightRadius: 8,
                        }}
                      >
                        Action
                      </TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.length > 0 ? (
                    leads.map((lead) => {
                      const isDeleted = String(lead.isdeleted) === "1";

                      return (
                      <TableRow key={lead.candidateuuid}>
                        <TableCell
                          sx={{
                            flex: 1,
                            opacity: lead.isactive === 1 ? 1 : 0.4,
                          }}
                        >
                          {lead.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            flex: 1,
                            opacity: lead.isactive === 1 ? 1 : 0.4,
                          }}
                        >
                          {lead.email}
                        </TableCell>
                        <TableCell
                          sx={{
                            flex: 1,
                            opacity: lead.isactive === 1 ? 1 : 0.4,
                          }}
                        >
                          {lead?.enquirydate
                            ? dayjs(lead.enquirydate).format("LL")
                            : ""}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            opacity: lead.isactive === 1 ? 1 : 0.4,
                          }}
                        >
                          {lead.isactive === 1 ? "Active" : "Inactive"}
                          {" / "}
                          <Box
                            component="span"
                            sx={{
                              color: isDeleted ? "#D32F2F" : "inherit",
                              fontWeight: isDeleted ? 600 : 400,
                            }}
                          >
                            {isDeleted ? "Deleted" : "Not deleted"}
                          </Box>
                        </TableCell>
                        {isAdmin ? (
                          <TableCell
                            sx={{
                              opacity: lead.isactive === 1 ? 1 : 0.4,
                            }}
                          >
                            {getAssigneeName(lead.ownerid)}
                          </TableCell>
                        ) : null}
                        {role !== "agent" ? (
                          <TableCell>
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/leads/lead-details/${lead.candidateuuid}`,
                                  {
                                    state: { lead: lead },
                                  }
                                )
                              }
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                        ) : null}
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center">
                        No leads found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 0 ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  py: 2,
                  px: 1,
                  borderTop: "1px solid #E8E8E8",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, value) => onPageChange?.(value - 1)}
                  disabled={isTableLoading}
                  showFirstButton
                  showLastButton
                  boundaryCount={5}
                  siblingCount={1}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      minWidth: 32,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#272727",
                      border: "none",
                      borderRadius: "4px",
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#3A80F7",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#2f6ad6",
                      },
                    },
                    "& .MuiPaginationItem-root.Mui-disabled": {
                      opacity: 0.4,
                    },
                  }}
                />
              </Stack>
            ) : null}
          </>
        )}
      </Box>
    </>
  );
};
